import React from 'react';
import { motion } from 'framer-motion';
import { PhoneForwardedIcon, ShieldCheckIcon, ZapIcon, SmartphoneIcon, ArrowRightIcon, PhoneCallIcon, MessageSquareIcon, ClockIcon } from 'lucide-react';
export function DigitalPhoneLinesPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <div className="bg-white" data-id="element-1196">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-slate-900" data-id="element-1197">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-1198"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-1199"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1200">
          <div className="max-w-3xl" data-id="element-1201">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-1202">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-1203">
                <span className="flex h-2 w-2 rounded-full bg-amber-400 mr-2" data-id="element-1204"></span>
                Professional Voice Connectivity
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-1205">
                Digital Phone
                <br data-id="element-1206" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-dark" data-id="element-1207">
                  Lines
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8" data-id="element-1208">
                Upgrade your business with professional digital phone lines.
                Enjoy advanced features, crystal-clear quality, and the
                reliability your business demands.
              </p>
              <button onClick={() => onNavigate('contact')} className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-1209">
                Get Started Today
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1210" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-white" data-id="element-1211">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1212">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-1213">
            {[{
            title: 'Advanced Features',
            desc: 'Includes call forwarding, voicemail-to-email, caller ID, and automated attendants as standard.',
            icon: ZapIcon,
            color: 'text-amber-500',
            bg: 'bg-amber-50'
          }, {
            title: 'Mobility',
            desc: 'Take your business line anywhere. Reroute calls to your mobile device seamlessly when you leave the office.',
            icon: SmartphoneIcon,
            color: 'text-blue-500',
            bg: 'bg-blue-50'
          }, {
            title: 'Reliability',
            desc: 'Digital lines offer superior uptime and quality compared to traditional analog phone service.',
            icon: ShieldCheckIcon,
            color: 'text-emerald-500',
            bg: 'bg-emerald-50'
          }].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="flex flex-col items-start" data-id="element-1214">
                <div className={`w-14 h-14 ${feature.bg} rounded-2xl flex items-center justify-center mb-6`} data-id="element-1215">
                  <feature.icon className={`w-7 h-7 ${feature.color}`} data-id="element-1216" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-1217">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-1218">{feature.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Feature List */}
      <section className="py-24 bg-slate-50" data-id="element-1219">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1220">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1221">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1222">
              Everything Your Business Needs
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1223">
              Our digital phone lines come packed with professional features to
              help you manage your calls effectively.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1224">
            {[{
            title: 'Call Forwarding',
            icon: PhoneForwardedIcon
          }, {
            title: 'Visual Voicemail',
            icon: MessageSquareIcon
          }, {
            title: 'HD Voice Quality',
            icon: PhoneCallIcon
          }, {
            title: '24/7 Support',
            icon: ClockIcon
          }].map((item, i) => <div key={i} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm text-center" data-id="element-1225">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-4" data-id="element-1226">
                  <item.icon className="w-6 h-6 text-amber-600" data-id="element-1227" />
                </div>
                <h4 className="font-bold text-slate-900" data-id="element-1228">{item.title}</h4>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary" data-id="element-1229">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1230">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1231">
            Professional Voice for Your Business
          </h2>
          <p className="text-xl text-blue-100 mb-10" data-id="element-1232">
            Upgrade to digital phone lines and never miss an important call
            again.
          </p>
          <button onClick={() => onNavigate('contact')} className="bg-white text-primary hover:bg-slate-50 px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg" data-id="element-1233">
            Get a Quote
          </button>
        </div>
      </section>
    </div>;
}